import { supabase } from '../supabaseClient';

const DOC_CONTEXT_COLUMNS = 'doc_id, goal_state, source_fingerprint, doc_summary_md, chunk_summaries, summary_state, created_at, updated_at';
const PROJECT_CONTEXT_COLUMNS = 'project_id, summary_md, source_fingerprint, summary_state, created_at, updated_at';

const isMissingRelationError = (error = null, tableName = '') => {
  const code = String(error?.code || '').trim();
  const message = String(error?.message || '').toLowerCase();
  return code === '42P01' || (message.includes('relation') && message.includes(String(tableName || '').toLowerCase()));
};

const normalizeDocContextRow = (data = null) => {
  if (!data) return null;
  return {
    docId: Number(data.doc_id),
    goalState: data.goal_state && typeof data.goal_state === 'object' ? data.goal_state : null,
    sourceFingerprint: typeof data.source_fingerprint === 'string' ? data.source_fingerprint : null,
    docSummaryMd: typeof data.doc_summary_md === 'string' ? data.doc_summary_md : null,
    chunkSummaries: Array.isArray(data.chunk_summaries) ? data.chunk_summaries : [],
    summaryState: data.summary_state && typeof data.summary_state === 'object' ? data.summary_state : {},
    createdAt: data.created_at || null,
    updatedAt: data.updated_at || null
  };
};

const normalizeProjectContextRow = (data = null) => {
  if (!data) return null;
  return {
    projectId: Number(data.project_id),
    summaryMd: typeof data.summary_md === 'string' ? data.summary_md : null,
    sourceFingerprint: typeof data.source_fingerprint === 'string' ? data.source_fingerprint : null,
    summaryState: data.summary_state && typeof data.summary_state === 'object' ? data.summary_state : {},
    createdAt: data.created_at || null,
    updatedAt: data.updated_at || null
  };
};

export async function apiLoadDocContext({ docId } = {}) {
  const normalizedDocId = Number(docId);
  if (!Number.isFinite(normalizedDocId) || normalizedDocId <= 0) return null;

  const { data, error } = await supabase
    .from('doc_context')
    .select(DOC_CONTEXT_COLUMNS)
    .eq('doc_id', normalizedDocId)
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error, 'doc_context')) return null;
    throw error;
  }

  return normalizeDocContextRow(data);
}

export async function apiUpsertDocContext({
  docId,
  goalState,
  sourceFingerprint,
  docSummaryMd,
  chunkSummaries,
  summaryState
} = {}) {
  const normalizedDocId = Number(docId);
  if (!Number.isFinite(normalizedDocId) || normalizedDocId <= 0) return null;

  const payload = {
    doc_id: normalizedDocId,
    updated_at: new Date().toISOString()
  };

  if (goalState !== undefined) {
    payload.goal_state = goalState && typeof goalState === 'object' ? goalState : {};
  }

  if (sourceFingerprint !== undefined) {
    payload.source_fingerprint = typeof sourceFingerprint === 'string' && sourceFingerprint.trim()
      ? sourceFingerprint
      : null;
  }

  if (docSummaryMd !== undefined) {
    payload.doc_summary_md = typeof docSummaryMd === 'string' && docSummaryMd.trim()
      ? docSummaryMd
      : null;
  }

  if (chunkSummaries !== undefined) {
    payload.chunk_summaries = Array.isArray(chunkSummaries) ? chunkSummaries : [];
  }

  if (summaryState !== undefined) {
    payload.summary_state = summaryState && typeof summaryState === 'object' ? summaryState : {};
  }

  const { data, error } = await supabase
    .from('doc_context')
    .upsert([payload], { onConflict: 'doc_id' })
    .select(DOC_CONTEXT_COLUMNS)
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error, 'doc_context')) return null;
    throw error;
  }

  return normalizeDocContextRow(data);
}

export async function apiLoadProjectContext({ projectId } = {}) {
  const normalizedProjectId = Number(projectId);
  if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) return null;

  const { data, error } = await supabase
    .from('project_context')
    .select(PROJECT_CONTEXT_COLUMNS)
    .eq('project_id', normalizedProjectId)
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error, 'project_context')) return null;
    throw error;
  }

  return normalizeProjectContextRow(data);
}

export async function apiUpsertProjectContext({
  projectId,
  summaryMd,
  sourceFingerprint,
  summaryState
} = {}) {
  const normalizedProjectId = Number(projectId);
  if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) return null;

  const payload = {
    project_id: normalizedProjectId,
    updated_at: new Date().toISOString()
  };

  if (summaryMd !== undefined) {
    payload.summary_md = typeof summaryMd === 'string' && summaryMd.trim()
      ? summaryMd
      : null;
  }

  if (sourceFingerprint !== undefined) {
    payload.source_fingerprint = typeof sourceFingerprint === 'string' && sourceFingerprint.trim()
      ? sourceFingerprint
      : null;
  }

  if (summaryState !== undefined) {
    payload.summary_state = summaryState && typeof summaryState === 'object' ? summaryState : {};
  }

  const { data, error } = await supabase
    .from('project_context')
    .upsert([payload], { onConflict: 'project_id' })
    .select(PROJECT_CONTEXT_COLUMNS)
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error, 'project_context')) return null;
    throw error;
  }

  return normalizeProjectContextRow(data);
}
